import type { Strings } from './en';

/** A non-breaking space, before the French `:`, `?` and `!`. */
const _ = ' ';

const SINK_FR = {
  off: 'est poussé hors du lac',
  hole: 'tombe dans un trou',
  fall: 'tombe de la corniche et passe à travers la glace',
  crush: 'est écrasé contre la glace',
} as const;

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`;

/** Every word the site shows, in French. */
export const fr: Strings = {
  langName: 'Français',
  meta: {
    description: `Halvard${_}: une bataille viking sur un lac qui fond.`,
  },
  nav: {
    skip: 'Aller au contenu',
    home: 'Accueil Halvard',
    main: 'Menu principal',
    play: 'Bot',
    online: 'En ligne',
    learn: 'Règles',
    language: 'Langue',
  },
  titles: {
    playLocal: 'Jouer',
    playBot: 'Jouer contre le bot',
    botVsBot: 'Bot contre bot',
    playFriend: 'Jouer avec un ami',
    onlineMatch: 'Match en ligne',
    learn: 'Règles',
    notFound: 'Hors du sentier',
  },
  home: {
    tagline: 'Une bataille viking sur un lac qui fond.',
    lead: `Poussez l’ennemi à travers la glace et prenez les trois pierres runiques avant le dégel. Chaque lac est nouveau${_}: aucune ouverture à apprendre par cœur.`,
    playBot: 'Jouer contre le bot',
    playOnline: 'Jouer avec un ami en ligne',
    playLocal: 'À deux sur un écran',
    learn: 'Apprendre les règles',
    picture: `Une partie en cours sur le lac gelé${_}: les guerriers orange de Brand et bleu-vert de Rime sur les cases de glace, la rive et une maison longue derrière.`,
    factsTitle: 'Comment ça se joue',
    facts: [
      {
        image: '/shove.webp',
        title: 'Pousser, pas prendre',
        body: `Choisissez un guerrier${_}: chaque ennemi à portée affiche votre force contre son mur de boucliers. 3:2 passe, le gris échoue. Le guerrier pâle montre où l’ennemi va glisser.`,
        alt: `Gros plan${_}: un Huscarl de Brand face à un Lancier de Rime, un badge rose 3:2 au-dessus et une copie pâle du Lancier une case plus loin.`,
      },
      {
        image: '/crack.webp',
        title: 'La glace fond',
        body: 'Toutes les trois manches, deux cases se fissurent. Les fissures rouges s’ouvrent en trou trois manches plus tard et emportent qui s’y trouve.',
        alt: `Gros plan${_}: une case de glace traversée d’une fissure rouge.`,
      },
      {
        image: '/runestone.webp',
        title: 'Tenez les pierres runiques',
        body: `Chaque pierre runique est une plaque entière de 3×3 cases, bordée de la couleur du camp qui la tient. Brand doit en tirer 20 de Gloire en 16 manches${_}; Rime n’a qu’à tenir.`,
        alt: `Gros plan${_}: la plaque d’une pierre runique bordée de bleu-vert, tenue par Rime.`,
      },
    ],
  },
  notFound: {
    title: 'Hors du sentier',
    body: 'Cette page n’existe pas.',
    back: 'Retour à l’accueil',
  },

  sides: { brand: 'Brand', rime: 'Rime' },
  warriors: { skier: 'Skieur', spear: 'Lancier', huscarl: 'Huscarl' },
  warriorName: (side, type) => `${type} de ${side}`,
  warriorRule: {
    skier: 'Force 1, 4 points de mouvement. Rapide, mais facile à pousser.',
    spear: 'Force 2, 3 points de mouvement.',
    huscarl: 'Force 3, 2 points de mouvement. Lent et lourd.',
  },
  runestones: ['Pierre runique ouest', 'Pierre runique centrale', 'Pierre runique est'],
  bots: {
    easy: {
      name: 'Facile',
      detail: 'Voit un coup à l’avance, et votre poussée la plus dangereuse.',
    },
    normal: { name: 'Normal', detail: 'Voit son coup et votre réponse.' },
    hard: { name: 'Difficile', detail: 'Voit 2 coups à l’avance pour chaque camp.' },
    expert: { name: 'Expert', detail: 'Voit 3 coups à l’avance pour chaque camp. Lent.' },
  },

  game: {
    lakeArea: 'Lac',
    controls: 'Commandes de la partie',
    boardLabel: (seed, status) =>
      `Lac n° ${seed}. ${status}. Flèches pour se déplacer, Entrée pour choisir.`,
    lake: (seed) => `Lac n° ${seed}`,
    round: (round, of) => `Manche ${round} sur ${of}`,
    hide: 'Masquer',
    showControls: 'Commandes',
    players: 'Joueurs',
    you: 'Vous',
    opponent: 'Adversaire',
    bot: (level) => `Bot ${level.toLowerCase()}`,
    sidePlayer: (side) => `Joueur ${side}`,
    toMove: (side) => `À ${side} de jouer`,
    places: (side) => `${side} place un guerrier`,
    botTurn: (side, player, doing) =>
      `${side} (${player}) ${doing === 'thinking' ? 'réfléchit…' : doing === 'placing' ? 'place un guerrier' : 'joue'}`,
    glory: 'Gloire',
    gloryMeter: 'Gloire de Brand',
    gloryLine: (glory, target) => `Brand ${glory} sur ${target}`,
    gloryNeeds: (more, turns) => `${_}: encore ${more} en ${plural(turns, 'tour')}`,
    runestonesLabel: 'Pierres runiques',
    holds: (side) => `tenue par ${side}`,
    nobody: 'personne',
    placeHeading: 'Placer un guerrier',
    placeGroup: 'Guerrier à placer',
    placeHint: (side, ranks) =>
      `Cliquez une case libre du territoire de ${side} (rangées ${ranks}).`,
    shoveTitle: (target) => `Pousser ${target}`,
    shoveFrom: (from) => `depuis ${from}`,
    pointAnother: `Visez un autre losange pour pousser depuis là${_}; cliquez pour jouer.`,
    clickEnemy: 'Cliquez l’ennemi pour le choisir, puis d’où le pousser.',
    shoveFails: `Cette poussée échoue${_}: le mur de boucliers tient. Essayez une autre case.`,
    dotsHelp: `Points${_}: où il peut marcher. Chiffres sur un ennemi${_}: votre poussée contre son mur de boucliers.`,
    selectWarrior: 'Choisissez un de vos guerriers.',
    pass: 'Passer (aucun coup)',
    undo: 'Annuler',
    cracks: 'Fissures',
    groansAt: (round) => `La glace gronde à la fin de la manche ${round}.`,
    noneYet: 'Aucune pour l’instant.',
    crack: (round, opensHole) => [
      '',
      opensHole ? 'devient un trou' : 's’amincit',
      ` à la fin de la manche ${round}`,
    ],
    log: 'Journal',
    logEmpty: 'Brand place en premier, puis les camps jouent chacun leur tour.',
    moveLog: 'Journal des coups',
    view: 'Vue',
    cameraView: 'Vue de la caméra',
    views: { brand: 'Côté Brand', rime: 'Côté Rime', top: 'Dessus' },
    animations: 'Animations',
    motion: { auto: 'Auto', on: 'Oui', off: 'Non' },
    reducedNote: `Votre système demande de réduire les animations${_}: les guerriers se déplacent sans animation. Choisissez Oui pour les voir marcher.`,
    newLake: 'Nouveau lac',
    replay: 'Rejouer ce lac',
    gloryResult: (glory, target) => `Gloire${_}: ${glory} sur ${target}.`,
    changeBots: 'Changer de bots ou de camp',
  },

  board: {
    squareName: (name) => name,
    hole: 'trou',
    ice: (h) => `glace ${h}`,
    cracked: (opensHole, round) =>
      `fissurée, ${opensHole ? 'devient un trou' : 's’amincit'} à la fin de la manche ${round}`,
    runestone: (name) => name.toLowerCase(),
    walkHere: 'marcher ici',
    shoveHere: (strength, wall) => `pousser ${strength} contre ${wall}`,
    tone: {
      works: '',
      'sinks-enemy': ', fait couler un ennemi',
      'sinks-ally': ', fait couler un des vôtres',
      fails: ', échoue',
    },
    shoveFromHere: 'pousser depuis ici',
    sinkBadge: {
      off: 'hors du lac',
      hole: 'dans un trou',
      fall: 'chute de 2 niveaux',
      crush: 'écrasé',
    },
  },

  log: {
    offTheIce: 'hors du lac',
    force: (force, shields) =>
      shields > 0 ? `${force} de force + ${shields} de bouclier` : `${force} de force`,
    sums: (strength, wall, mine, theirs) => `${strength} contre ${wall} (${mine} contre ${theirs})`,
    placed: (warrior, sq) => `${warrior} prend position en ${sq}.`,
    moved: (warrior, from, to) => `${warrior} va de ${from} à ${to}.`,
    shoved: (warrior, target, sq, sums) => `${warrior} pousse ${target} en ${sq}${_}: ${sums}.`,
    sunk: (warrior, reason) => `${warrior} ${SINK_FR[reason]}.`,
    passes: (side) => `${side} ne peut pas bouger et passe.`,
    glory: (gained, total) => `Brand gagne ${gained} de Gloire (${total} en tout).`,
    givesWay: (sq, sunk) => `La glace cède en ${sq}${sunk ? `${_}: ${sunk} passe dessous` : ''}.`,
    thins: (sq, height) => `La glace s’amincit à ${height} en ${sq}.`,
    groans: (squares) =>
      `La glace gronde${_}: ${squares.join(' et ')} ${squares.length > 1 ? 'se fissurent' : 'se fissure'}.`,
    undone: (notation, next) => `Annulé${_}: ${notation}. ${next}.`,
    lakeStart: (seed) => `Lac n° ${seed}. Brand place un guerrier en premier.`,
    lakeNow: (seed, next) => `Lac n° ${seed}. ${next}.`,
    shoveFailsNow: (strength, wall) =>
      `Cette poussée échoue${_}: ${strength} contre ${wall}. Le mur de boucliers tient.`,
    chooseFrom: 'Choisissez la case d’où pousser.',
  },

  outcome: {
    wins: (side) => `${side} gagne`,
    glory: 'Brand a tiré assez de puissance des pierres runiques.',
    held: 'Rime a tenu le lac jusqu’au dégel.',
    sunk: (loser) => `Tous les guerriers de ${loser} ont coulé.`,
    resign: (loser) => `${loser} a abandonné.`,
  },

  match: {
    you: 'Vous',
    host: 'L’hôte',
    opponent: 'Votre adversaire',
    resigned: (who, defenderQuit, round) =>
      `${who} ${who === 'Vous' ? 'attaquiez' : 'attaquait'}${_}; ${defenderQuit ? 'le défenseur' : 'l’attaquant'} a abandonné à la manche ${round}`,
    sankAll: (who, round) =>
      `${who} ${who === 'Vous' ? 'avez' : 'a'} fait couler tous les défenseurs à la manche ${round}`,
    reached: (who, glory, round) =>
      `${who} ${who === 'Vous' ? 'avez' : 'a'} atteint ${glory} de Gloire à la manche ${round}`,
    lostAll: (who, round) =>
      who === 'Vous'
        ? `Vous avez perdu tous vos guerriers à la manche ${round}`
        : `${who} a perdu tous ses guerriers à la manche ${round}`,
    heldTo: (who, isYou, glory) =>
      `${who} ${isYou ? 'avez été tenu' : 'a été tenu'} à ${glory} de Gloire`,
    drawn: 'Match nul',
    youWin: 'Vous gagnez le match',
    youLose: 'Vous perdez le match',
    theyWin: (name) => `${name} gagne le match`,
    theWinner: 'Le vainqueur',
    byResign: 'Un joueur a abandonné.',
    byAttackOne: 'Une seule attaque a pris les pierres runiques.',
    byAttackSingle: 'La partie a tranché.',
    fasterRounds: (fast, slow) =>
      `Les deux attaques ont réussi${_}; la plus rapide gagne (manche ${fast} contre manche ${slow}).`,
    fasterSame: `Les deux attaques ont réussi à la même manche${_}; la plus grande Gloire gagne.`,
    moreGlory: (most, least) =>
      `Les deux attaques ont été tenues${_}; la plus grande Gloire gagne (${most} contre ${least}).`,
    moreGlorySingle: 'La plus grande Gloire gagne.',
    level: 'Rien n’a départagé les deux attaques.',
  },

  online: {
    title: 'Jouer avec un ami',
    intro:
      'Créez une partie, puis envoyez le lien à votre ami. Celui qui l’ouvre prend l’autre camp.',
    format: 'Format',
    matchTitle: 'Match (conseillé)',
    matchDetail: `Deux parties sur le même lac${_}: chacun attaque une fois.`,
    singleTitle: 'Partie simple',
    singleDetail: `Une partie${_}: l’un attaque, l’autre défend.`,
    attackFirst: `Vous attaquez en premier${_}?`,
    yourSide: 'Votre camp',
    random: 'Au hasard',
    randomDetail: 'Laissez la glace décider.',
    brandDetail: `Attaque${_}: prendre les pierres runiques.`,
    rimeDetail: 'Défendre jusqu’au dégel.',
    create: 'Créer et obtenir un lien',
    outdatedTitle: 'Cette page n’est plus à jour',
    outdatedBody: 'Le serveur de jeu a été mis à jour. Rechargez la page pour continuer.',
    reload: 'Recharger',
    offline: 'Impossible de joindre le serveur de jeu',
    connecting: 'Connexion…',
    tryingAgain: 'Nouvel essai…',
    swap: (first, youAttack) =>
      `${first ? `${first}. ` : ''}Les camps s’inversent${youAttack === null ? '' : `${_}: vous ${youAttack ? 'attaquez' : 'défendez'}`}.`,
    startGame2: 'Lancer la partie 2',
    newMatch: 'Nouveau match',
    waitingTitle: 'En attente de votre ami',
    waitingDetail: 'Envoyez-lui ce lien. La partie commence quand il l’ouvre.',
    inviteLink: 'Lien d’invitation',
    copied: 'Copié',
    copy: 'Copier le lien',
    matchGame: (n) => `Match · partie ${n} sur 2`,
    singleGame: 'Partie simple',
    waitingName: 'En attente…',
    youSuffix: ' (vous)',
    away: 'absent',
    youThisGame: (attack, side) =>
      `Vous ${attack ? 'attaquez' : 'défendez'} dans cette partie (${side}).`,
    opponentAway: ' Votre adversaire est absent.',
    watching: 'Vous regardez.',
    gameN: (n, text) => `Partie ${n}${_}: ${text}.`,
    reconnecting: 'Reconnexion…',
    confirmResign: (match) => `Confirmer${_}: abandonner${match ? ' le match' : ''}`,
    resign: 'Abandonner',
    nowWatching: (why) => `${why}${_}: vous regardez.`,
    errors: {
      GAME_OVER: 'La partie est terminée',
      NOT_YOUR_TURN: 'Ce n’est pas votre tour',
      ILLEGAL_ACTION: 'Ce coup n’est pas permis',
      GAME_NOT_FOUND: 'Ce match n’existe pas, ou il est terminé',
      NOT_A_PLAYER: 'Vous ne jouez pas dans ce match',
      BAD_JOIN_TOKEN: 'Ce lien d’invitation n’est pas valable',
      GAME_FULL: 'Cette invitation a déjà servi',
      STALE_SEQ: `La partie a avancé${_}; mise à jour`,
      RATE_LIMITED: `Trop de messages${_}; ralentissez`,
      WAITING_FOR_OPPONENT: 'Attendez que votre ami arrive',
      CLIENT_OUTDATED: 'Cette page n’est plus à jour',
      INTERNAL: 'Le serveur de jeu a eu un problème',
    },
  },

  botSetup: {
    title: 'Jouer contre le bot',
    you: 'Vous',
    brandTitle: 'Jouer Brand',
    brandDetail: `Vous attaquez${_}: prenez les pierres runiques avant le dégel.`,
    rimeTitle: 'Jouer Rime',
    rimeDetail: `Vous défendez${_}: tenez le lac jusqu’à la manche 16.`,
    watchTitle: 'Regarder deux bots',
    watchDetail: 'Les deux camps sont joués par des bots.',
    botOf: (side) => `Bot de ${side}`,
    start: 'Commencer',
  },
};
